import { useEffect, useRef } from 'react';
import { AppStorage } from '../Basic/storage-service';

const parseJwt = (token) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        return JSON.parse(jsonPayload);
    } catch (e) {
        return null;
    }
};

// onLogout: called when the JWT is expired beyond the grace period.
// onWarn (optional): called ONCE when the JWT has just expired but is still within
// the grace period, so the UI can nudge the user (e.g. a toast) without logging out.
export const useTokenExpiry = (onLogout, onWarn) => {
    const isFirstRun = useRef(true);
    const hasWarnedRef = useRef(false);
    const isLoggedIn = AppStorage.isLoggedIn();

    useEffect(() => {
        if (!isLoggedIn) return;

        const checkToken = () => {
            const token = AppStorage.getToken();
            if (!token) return;

            const payload = parseJwt(token);
            // Skip non-JWT / opaque tokens silently (no exp to reason about).
            if (!payload || !payload.exp) return;

            const currentTime = Math.floor(Date.now() / 1000);
            const expiryTime = payload.exp;
            const gracePeriod = 5 * 60; // 5 minutes grace period

            if (currentTime > (expiryTime + gracePeriod)) {
                console.warn('[SECURITY] Token has expired beyond the grace period. Logging out.');
                onLogout();
            } else if (currentTime > expiryTime) {
                // Recently expired, still within grace: warn only (once), don't log out.
                if (!hasWarnedRef.current) {
                    hasWarnedRef.current = true;
                    console.warn('[SECURITY] Token has expired; within grace period. Warning user.');
                    if (typeof onWarn === 'function') onWarn();
                }
            } else {
                // Token valid again (e.g. refreshed) — allow a future warning.
                hasWarnedRef.current = false;
            }
        };

        const interval = setInterval(() => {
            if (isFirstRun.current) {
                isFirstRun.current = false;
                return; // Skip checking on mount to prevent false-positives during reload
            }
            checkToken();
        }, 60000); // Check every 60 seconds

        // Re-check immediately when the app is foregrounded (mobile / Android Home
        // button). Timers are frozen while backgrounded, so a token that expired
        // during that time would otherwise linger up to 60s after return.
        //
        // Deliberately NOT bound to plain `pageshow`: that fires on every normal
        // load and would reintroduce the mount-time reload-race the isFirstRun
        // guard protects against. visibilitychange only fires on real transitions
        // (never the initial visible load), and pageshow is used only for bfcache
        // restores (event.persisted) — both safe.
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') checkToken();
        };
        const handlePageShow = (event) => {
            if (event.persisted) checkToken();
        };
        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('pageshow', handlePageShow);

        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('pageshow', handlePageShow);
        };
    }, [isLoggedIn, onLogout, onWarn]);
};
